import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { AlertCircle, Eye, EyeOff, Shield } from 'lucide-react';

const LOGO_URL = "/logo.png";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { adminLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setError('');
    setSubmitting(true);
    try {
      await adminLogin(email, password);
      setError('');
      navigate('/admin', { replace: true });
      return;
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Ugyldige loginoplysninger');
    } finally {
      if (mountedRef.current) {
        setSubmitting(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
      <div className="max-w-sm w-full animate-fade-in">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Molin30" className="h-12 mx-auto mb-6" />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs font-bold uppercase tracking-[0.15em] mb-4">
            <Shield className="w-3.5 h-3.5" /> Adminadgang
          </div>
          <h1 className="text-2xl font-semibold text-slate-50 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Admin-login</h1>
          <p className="text-slate-400 text-sm">Kun for autoriseret personale.</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-6" data-testid="admin-login-error">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-400">{error}</span>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="text-sm text-slate-400 block mb-1.5">E-mail</label>
            <input
              type="email"
              placeholder="admin@molin30.dk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-2.5 outline-none placeholder:text-slate-600"
              data-testid="admin-login-email"
              required
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 block mb-1.5">Adgangskode</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-2.5 outline-none pr-10"
                data-testid="admin-login-password"
                required
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" disabled={submitting} className="w-full bg-purple-600 hover:bg-purple-500 text-white font-medium py-3 rounded-md disabled:opacity-50" data-testid="admin-login-submit">
            {submitting ? 'Logger ind...' : 'Log ind'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button onClick={() => navigate('/login')} className="text-sm text-slate-500 hover:text-slate-300" data-testid="switch-to-guest-login">
            Gæstelogin
          </button>
        </div>
      </div>
    </div>
  );
}
